import type { GPSCoords } from "@/lib/image-utils";
import { createTargetSvg } from "@/lib/svg-utils";
import React, { useEffect, useRef, useState } from "react";
import { getConfig } from "@/config"; 

const GoogleMapPin: React.FC<{ coords: GPSCoords; onChange: (coords: GPSCoords) => void; onZoomChange?: (zoom: number) => void; setCurrentLocation: boolean;}> = ({ coords, onChange, onZoomChange, setCurrentLocation }) => {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const markerRef = React.useRef<google.maps.Marker | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const onChangeRef = useRef(onChange);
  const onZoomChangeRef = useRef(onZoomChange);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);  
  const [apiKey, setApiKey] = useState<string>("");

  onChangeRef.current = onChange;
  onZoomChangeRef.current = onZoomChange;

  useEffect(() => {
    const loadRuntimeConfig = async () => {
      try {
        // Make sure config is loaded (safe to call multiple times)
        if (!window.ENV) {
          // If you have a global loadConfig function, call it here
          // Otherwise assume it's already loaded by main.tsx
          console.warn("window.ENV not loaded yet");
        }
        const cfg = getConfig();           // ← This is safe now
        setApiKey(cfg.GOOGLE_MAPS_API_KEY || "");
        setConfigLoaded(true);
      } catch (error) {
        console.error("Failed to load Google Maps API key from config", error);
      }

      if (setCurrentLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            onChangeRef.current({ latitude, longitude });
          },
          () => {
            console.warn("Unable to retrieve your location.");
          }
        );
      };
    }

    loadRuntimeConfig();
  }, []);  

  useEffect(() => {
    if (!apiKey || !configLoaded) return;   // Wait for config

    if (window.google && window.google.maps) {
      setGoogleMapsLoaded(true);
      return;
    }

    const scriptId = "google-maps-script";
    if (document.getElementById(scriptId)) return;

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.onload = () => setGoogleMapsLoaded(true);
    document.body.appendChild(script);
  }, [apiKey, configLoaded]);

  useEffect(() => {  
    if (!googleMapsLoaded || !mapRef.current || !window.google || !window.google.maps) {
      return;
    }

    if (mapInstanceRef.current) {
      return;
    }

    const map = new google.maps.Map(mapRef.current, {
      center: { lat: coords.latitude!, lng: coords.longitude! },
      zoom: 18,
      mapTypeId: google.maps.MapTypeId.SATELLITE,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Add custom button to map controls
    const locationButton = document.createElement("button");
    locationButton.type = "button";
    locationButton.className = "text-center bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 p-2 z-10";
    locationButton.style.margin = "8px";
    locationButton.insertBefore(createTargetSvg(), locationButton.firstChild);
    locationButton.onclick = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            onChangeRef.current({ latitude, longitude });
          },
          () => {
            alert("Unable to retrieve your location.");
          }
        );
      } else {
        alert("Geolocation is not supported by your browser.");
      }
    };
    map.controls[google.maps.ControlPosition.RIGHT_CENTER].push(locationButton);
    const marker = new google.maps.Marker({
      position: { lat: coords.latitude!, lng: coords.longitude! },
      map,
      draggable: true,
    });

    map.addListener("zoom_changed", () => {
      if (!onZoomChangeRef.current) return;
      const newZoom = map.getZoom();
      onZoomChangeRef.current(newZoom!);
    });

    marker.addListener("dragend", (e: google.maps.MapMouseEvent) => {
      const newLat = e.latLng?.lat();
      const newLng = e.latLng?.lng();
      if (newLat !== undefined && newLng !== undefined) {
        onChangeRef.current({ latitude: newLat, longitude: newLng });
      }
    });
    mapInstanceRef.current = map;
    markerRef.current = marker;
    setMapLoaded(true);

    return () => {
      marker.setMap(null);
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [googleMapsLoaded]);

  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current || coords.latitude == null || coords.longitude == null) {
      return;
    }

    const position = { lat: coords.latitude, lng: coords.longitude };
    markerRef.current.setPosition(position);
    mapInstanceRef.current.setCenter(position);
  }, [coords.latitude, coords.longitude]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={mapRef}
        style={{ width: "100%", height: "400px", borderRadius: "8px" }}
      />
      {(!mapLoaded || !googleMapsLoaded) && (
        <div className="text-center text-gray-500">Loading Google Maps...</div>
      )}
    </div>
  );
};

export default GoogleMapPin;
